import React from 'react';

const RESOURCE_SECTIONS = [
  {
    id: 'start',
    width: 'half',
    variant: 'fundamentals',
    title: 'Start here',
    blurb: 'Concrete paths for going from zero to writing and running your first circuits.',
    items: [
      {
        title: 'Quantum Computing for the Very Curious',
        meta: 'Michael Nielsen & Andy Matuschak • Interactive essay',
        description:
          'Step-by-step explanations with embedded questions—one of the best free introductions to qubits and measurement.',
        url: 'https://quantum.country/qcvc',
      },
      {
        title: 'Quantum Computing Explained',
        meta: 'Veritasium • Video • 22 min',
        description:
          'Accessible overview of superposition, entanglement, and why quantum computers are hard to build.',
        url: 'https://www.youtube.com/watch?v=JhHMJCUmq28',
      },
      {
        title: 'IBM Quantum Learning',
        meta: 'IBM • Courses & notebooks',
        description:
          'Free learning hub with structured modules you can run in the browser alongside QubitCompile problems.',
        url: 'https://quantum.cloud.ibm.com/learning/en',
      },
      {
        title: 'Quantum computing fundamentals (Microsoft Learn)',
        meta: 'Microsoft • Training path',
        description:
          'Guided modules on qubits, gates, and Azure Quantum tooling—good if you want exercises beyond Python.',
        url: 'https://learn.microsoft.com/en-us/training/paths/quantum-computing-fundamentals/',
      },
      {
        title: 'QubitCompile Problems',
        meta: 'QubitCompile • Practice • In-app',
        description:
          'Apply what you learn by building circuits in the editor and passing graded test cases.',
        url: '/problems',
      },
    ],
  },
  {
    id: 'qubits',
    width: 'half',
    variant: 'fundamentals',
    title: 'Qubits & linear algebra',
    blurb: 'State vectors, bras/kets, tensor products, and the math every circuit is built on.',
    items: [
      {
        title: 'Essence of Linear Algebra',
        meta: '3Blue1Brown • Video series',
        description:
          'Visual intuition for vectors, bases, and linear transformations—directly applicable to quantum states.',
        url: 'https://www.youtube.com/playlist?list=PLZHQObOWTQDPD3MizzM2xVFitgF8hE_ab',
      },
      {
        title: 'Quantum states notebook collection',
        meta: 'Qiskit Textbook (GitHub) • Tutorials',
        description:
          'Jupyter notebooks on single- and multi-qubit states, the Bloch sphere, and entanglement—open and runnable.',
        url: 'https://github.com/Qiskit/textbook/tree/main/notebooks/ch-states',
      },
      {
        title: 'MIT OpenCourseWare: Quantum Information Science I',
        meta: 'MIT OCW • Course materials',
        description:
          'Lecture notes and problem sets on qubits, density matrices, and quantum channels at a rigorous pace.',
        url: 'https://ocw.mit.edu/courses/8-370x-quantum-information-science-i-spring-2018/',
      },
      {
        title: 'Quantum Entanglement & Bell’s Inequality',
        meta: 'Up and Atom • Video • 13 min',
        description:
          'Clear explanation of entanglement and why it is not classical correlation—pairs well with multi-qubit circuits.',
        url: 'https://www.youtube.com/watch?v=ZozsEPaXCuA',
      },
      {
        title: 'Bloch sphere',
        meta: 'Wikipedia • Reference',
        description:
          'Compact reference for visualizing single-qubit states and common gates as rotations on the sphere.',
        url: 'https://en.wikipedia.org/wiki/Bloch_sphere',
      },
    ],
  },
  {
    id: 'gates',
    width: 'half',
    variant: 'gates',
    title: 'Quantum gates',
    blurb: 'Unitary operations on qubits: Pauli, Hadamard, phase, rotations, and multi-qubit controls.',
    items: [
      {
        title: 'Introduction to quantum gates',
        meta: 'Qiskit Textbook (GitHub) • Notebook',
        description:
          'Walkthrough of Pauli, Hadamard, phase, and rotation gates with matrix definitions and examples.',
        url: 'https://github.com/Qiskit/textbook/blob/main/notebooks/ch-gates/introduction.ipynb',
      },
      {
        title: 'Quantum logic gate',
        meta: 'Wikipedia • Reference',
        description:
          'Quick lookup for gate matrices, universal gate sets, and common circuit identities.',
        url: 'https://en.wikipedia.org/wiki/Quantum_logic_gate',
      },
      {
        title: 'Quantum gates explained',
        meta: 'Looking Glass Universe • Video • 19 min',
        description:
          'Hand-drawn explanations of X, H, CNOT, and phase gates—useful before tackling gate-heavy problems.',
        url: 'https://www.youtube.com/watch?v=8-6-hoyZ6mQ',
      },
      {
        title: 'Controlled NOT gate',
        meta: 'Wikipedia • Reference',
        description:
          'How CNOT flips a target conditioned on control—foundation for oracles and entangling circuits.',
        url: 'https://en.wikipedia.org/wiki/Controlled_NOT_gate',
      },
      {
        title: 'Quirk (browser circuit simulator)',
        meta: 'Algassert • Interactive',
        description:
          'Drag-and-drop simulator for small circuits—similar spirit to the in-app QubitCompile simulator.',
        url: 'https://algassert.com/quirk',
      },
      {
        title: 'Introduction to quantum computing (Quantum Inspire)',
        meta: 'QuTech • Tutorial',
        description:
          'Gentle introduction covering kets, gates, and circuits in a hardware-agnostic style.',
        url: 'https://www.quantum-inspire.com/kbase/introduction-to-quantum-computing',
      },
    ],
  },
  {
    id: 'circuits',
    width: 'half',
    variant: 'circuits',
    title: 'Circuits & measurement',
    blurb: 'From unitary evolution to readout statistics, shot counts, and verifying outputs.',
    items: [
      {
        title: 'Born rule',
        meta: 'Wikipedia • Reference',
        description:
          'How measurement probabilities come from amplitude squared—maps directly to expected output distributions in problems.',
        url: 'https://en.wikipedia.org/wiki/Born_rule',
      },
      {
        title: 'Quantum measurement',
        meta: 'Khan Academy • Article',
        description:
          'Short primer on measurement in quantum mechanics—helpful if the linear-algebra formalism feels abstract.',
        url: 'https://www.khanacademy.org/science/physics/quantum-physics',
      },
      {
        title: 'Qiskit SDK documentation',
        meta: 'IBM • Documentation',
        description:
          'Official docs for building circuits, transpiling, and running jobs—the API QubitCompile solutions target.',
        url: 'https://quantum.cloud.ibm.com/docs/en',
      },
      {
        title: 'IBM Quantum Composer',
        meta: 'IBM • Interactive',
        description:
          'Browser composer with drag-and-drop gates and simulation—compare outputs with your Python solutions.',
        url: 'https://quantum.cloud.ibm.com/composer',
      },
      {
        title: 'QubitCompile Quantum Simulator',
        meta: 'QubitCompile • Interactive • In-app',
        description:
          'Build and inspect circuits visually, then translate ideas into code for the problem editor.',
        url: '/simulator',
      },
      {
        title: 'Quantum circuit',
        meta: 'Wikipedia • Reference',
        description:
          'Circuit model, measurement at end of computation, and how unitary layers compose.',
        url: 'https://en.wikipedia.org/wiki/Quantum_circuit',
      },
    ],
  },
  {
    id: 'algorithms',
    width: 'full',
    variant: 'algorithms',
    title: 'Quantum algorithms',
    blurb:
      'Major algorithm families: search, factoring, period finding, Fourier transforms, and NISQ-era variational methods.',
    items: [
      {
        title: "Grover's algorithm",
        meta: 'Wikipedia • Overview',
        description:
          'Quadratic speedup for unstructured search—read before implementing oracle + diffuser patterns.',
        url: 'https://en.wikipedia.org/wiki/Grover%27s_algorithm',
      },
      {
        title: "Grover's algorithm notebook",
        meta: 'Qiskit Textbook (GitHub) • Tutorial',
        description:
          'Step-by-step implementation of amplitude amplification with a worked search example.',
        url: 'https://github.com/Qiskit/textbook/blob/main/notebooks/ch-algorithms/grover.ipynb',
      },
      {
        title: "Shor's algorithm",
        meta: 'Wikipedia • Overview',
        description:
          'Order finding and factoring at a high level—the canonical exponential-speedup example.',
        url: 'https://en.wikipedia.org/wiki/Shor%27s_algorithm',
      },
      {
        title: "Shor's algorithm notebook",
        meta: 'Qiskit Textbook (GitHub) • Tutorial',
        description:
          'Notebook walkthrough tying period finding to the quantum Fourier transform.',
        url: 'https://github.com/Qiskit/textbook/blob/main/notebooks/ch-algorithms/shor.ipynb',
      },
      {
        title: "Shor's algorithm explained",
        meta: 'Qiskit • Video • 18 min',
        description:
          'Video walkthrough of the factoring pipeline—pairs with the notebook and Shor-style problems.',
        url: 'https://www.youtube.com/watch?v=wUwZZaI5u0c',
      },
      {
        title: 'Quantum Fourier transform',
        meta: 'Qiskit Textbook (GitHub) • Notebook',
        description:
          'Circuit construction for QFT—the subroutine inside Shor and phase estimation.',
        url: 'https://github.com/Qiskit/textbook/blob/main/notebooks/ch-algorithms/quantum-fourier-transform.ipynb',
      },
      {
        title: 'Deutsch–Jozsa algorithm',
        meta: 'Qiskit Textbook (GitHub) • Notebook',
        description:
          'Constant-query versus classical exponential worst case—good first “oracle” algorithm.',
        url: 'https://github.com/Qiskit/textbook/blob/main/notebooks/ch-algorithms/deutsch-jozsa.ipynb',
      },
      {
        title: "Simon's algorithm",
        meta: 'Qiskit Textbook (GitHub) • Notebook',
        description:
          'Hidden-period problem that inspired Shor—practice with Fourier sampling on a small instance.',
        url: 'https://github.com/Qiskit/textbook/blob/main/notebooks/ch-algorithms/simon.ipynb',
      },
      {
        title: 'Quantum phase estimation',
        meta: 'Qiskit Textbook (GitHub) • Notebook',
        description:
          'Extract eigenphases of unitaries—backbone of chemistry and fault-tolerant algorithms.',
        url: 'https://github.com/Qiskit/textbook/blob/main/notebooks/ch-algorithms/quantum-phase-estimation.ipynb',
      },
      {
        title: 'Variational quantum eigensolver (original paper)',
        meta: 'Peruzzo et al. • arXiv paper',
        description:
          'Foundational VQE paper for near-term chemistry—read before hybrid optimization notebooks.',
        url: 'https://arxiv.org/abs/1304.3061',
      },
      {
        title: 'Quantum approximate optimization (QAOA)',
        meta: 'Qiskit Textbook (GitHub) • Notebook',
        description:
          'Layered ansatz for combinatorial problems on NISQ hardware.',
        url: 'https://github.com/Qiskit/textbook/blob/main/notebooks/ch-applications/qaoa.ipynb',
      },
      {
        title: 'Quantum Algorithm Zoo',
        meta: 'Stephen Jordan • Reference catalog',
        description:
          'Comprehensive list of known quantum algorithms and proven speedups.',
        url: 'https://quantumalgorithmzoo.org/',
      },
    ],
  },
  {
    id: 'tools',
    width: 'half',
    variant: 'tools',
    title: 'Interactive tools',
    blurb: 'Simulators, visualizers, and sandboxes for testing ideas before you commit them to code.',
    items: [
      {
        title: 'Quantum Flytrap',
        meta: 'Quantum Flytrap • Interactive',
        description:
          'Optical-style circuit builder with clear measurement readouts—great for superposition and interference.',
        url: 'https://quantumflytrap.com/',
      },
      {
        title: 'Quantum Inspire',
        meta: 'QuTech • Simulator + hardware',
        description:
          'Run small circuits in the browser and explore QuTech’s teaching materials.',
        url: 'https://www.quantum-inspire.com/',
      },
      {
        title: 'Quirk',
        meta: 'Algassert • Interactive',
        description:
          'Fast drag-and-drop simulator—ideal for sanity-checking gates before coding them in Qiskit.',
        url: 'https://algassert.com/quirk',
      },
      {
        title: 'IBM Quantum Composer',
        meta: 'IBM • Interactive',
        description:
          'Official IBM circuit builder with simulation and optional hardware submission.',
        url: 'https://quantum.cloud.ibm.com/composer',
      },
      {
        title: 'Quantum Open Source Foundation',
        meta: 'QOSF • Community hub',
        description:
          'Benchmarks, open-source projects, and tooling directories beyond a single vendor stack.',
        url: 'https://qosf.org/',
      },
    ],
  },
  {
    id: 'frameworks',
    width: 'half',
    variant: 'tools',
    title: 'Framework documentation',
    blurb: 'Official docs for the stacks you are most likely to use in coursework, research, and industry.',
    items: [
      {
        title: 'Qiskit SDK documentation',
        meta: 'IBM • Documentation',
        description:
          'API reference for circuits, transpiler, primitives, and runtime.',
        url: 'https://quantum.cloud.ibm.com/docs/en',
      },
      {
        title: 'Cirq (Google Quantum AI)',
        meta: 'Google • Documentation',
        description:
          'Python framework for NISQ circuits with strong Google hardware and simulation support.',
        url: 'https://quantumai.google/cirq',
      },
      {
        title: 'PennyLane documentation',
        meta: 'Xanadu • Documentation',
        description:
          'Differentiable quantum programming for ML, chemistry, and hybrid optimization.',
        url: 'https://docs.pennylane.ai/en/stable/',
      },
      {
        title: 'Azure Quantum & Q#',
        meta: 'Microsoft • Documentation',
        description:
          'Q# language, resource estimation, and cloud access to partner hardware.',
        url: 'https://learn.microsoft.com/en-us/azure/quantum/',
      },
      {
        title: 'Amazon Braket',
        meta: 'AWS • Documentation',
        description:
          'Managed access to IonQ, Rigetti, OQC, and simulators through AWS.',
        url: 'https://docs.aws.amazon.com/braket/',
      },
      {
        title: 'Mitiq (error mitigation)',
        meta: 'Unitary Fund • Documentation',
        description:
          'Zero-noise extrapolation and probabilistic error cancellation for noisy devices.',
        url: 'https://mitiq.readthedocs.io/en/stable/',
      },
    ],
  },
  {
    id: 'books',
    width: 'full',
    variant: 'books',
    layout: 'books',
    title: 'Essential books',
    blurb: 'Texts worth keeping on your desk once you move past introductory tutorials.',
    items: [
      {
        title: 'Quantum Computation and Quantum Information',
        authors: 'Nielsen & Chuang',
        publisher: 'Cambridge University Press',
        description:
          'The standard reference—linear algebra, circuits, algorithms, error correction, and complexity.',
        url: 'https://www.amazon.com/Quantum-Computation-Information-10th-Anniversary/dp/1107002176',
      },
      {
        title: 'Programming Quantum Computers',
        authors: 'Johnston, Harrigan, Gimeno-Segovia',
        publisher: "O'Reilly Media",
        description:
          'Practical guide to oracles, amplitude amplification, and programming patterns.',
        url: 'https://www.oreilly.com/library/view/programming-quantum-computers/9781492039679/',
      },
      {
        title: 'Quantum Computing: An Applied Approach',
        authors: 'Jack D. Hidary',
        publisher: 'Springer',
        description:
          'Algorithms, hardware, and industry context with Jupyter exercises.',
        url: 'https://link.springer.com/book/10.1007/978-3-030-83274-2',
      },
      {
        title: 'Quantum Computing: A Gentle Introduction',
        authors: 'Rieffel & Polak',
        publisher: 'MIT Press',
        description:
          'Accessible to computer scientists—circuits, algorithms, and limitations without heavy physics prerequisites.',
        url: 'https://mitpress.mit.edu/9780262015066/quantum-computing/',
      },
      {
        title: 'Microsoft Quantum Development Kit (open source)',
        authors: 'Microsoft',
        publisher: 'GitHub repository',
        description:
          'Source for Q# samples and katas—useful companion if you follow the Microsoft Learn path.',
        url: 'https://github.com/microsoft/Quantum',
      },
    ],
  },
  {
    id: 'reading',
    width: 'full',
    variant: 'reading',
    title: 'Deeper reading',
    blurb: 'Surveys, essays, and papers for context on hardware progress, limitations, and open problems.',
    items: [
      {
        title: 'Quantum Computing Report',
        meta: 'GQI • Industry reference',
        description:
          'Vendor landscape, funding, and roadmap context beyond textbook algorithms.',
        url: 'https://quantumcomputingreport.com/',
      },
      {
        title: 'The Case Against Quantum Computing',
        meta: 'IEEE Spectrum • Essay',
        description:
          'Gil Kalai’s skeptical perspective on noise thresholds—useful counterbalance to hype.',
        url: 'https://spectrum.ieee.org/the-case-against-quantum-computing',
      },
      {
        title: 'How Space and Time Could Be a Quantum Error-Correcting Code',
        meta: 'Quanta Magazine • Long read',
        description:
          'Accessible feature on holographic error correction and connections to fundamental physics.',
        url: 'https://www.quantamagazine.org/how-space-and-time-could-be-a-quantum-error-correcting-code-20190103/',
      },
      {
        title: 'Quantum error correction: an introductory guide',
        meta: 'arXiv • Survey paper',
        description:
          'Broad overview of fault-tolerance goals and experimental progress toward logical qubits.',
        url: 'https://arxiv.org/abs/2206.07587',
      },
      {
        title: 'Quantum machine learning: what quantum computing means to data mining',
        meta: 'Wittek • arXiv review',
        description:
          'Survey of how quantum linear algebra might accelerate ML tasks—open-access alternative to paywalled journals.',
        url: 'https://arxiv.org/abs/1803.04445',
      },
    ],
  },
  {
    id: 'quick',
    width: 'full',
    variant: 'quick',
    layout: 'pills',
    title: 'Framework quick access',
    blurb: null,
    items: [
      { name: 'Qiskit', url: 'https://www.ibm.com/quantum/qiskit', accentRgb: '107, 138, 154' },
      { name: 'IBM Quantum', url: 'https://quantum.cloud.ibm.com/', accentRgb: '90, 130, 160' },
      { name: 'Google Cirq', url: 'https://quantumai.google/cirq', accentRgb: '122, 154, 154' },
      { name: 'PennyLane', url: 'https://pennylane.ai/', accentRgb: '107, 138, 154' },
      { name: 'Microsoft Q#', url: 'https://azure.microsoft.com/en-us/solutions/quantum-computing/', accentRgb: '154, 138, 122' },
      { name: 'Amazon Braket', url: 'https://aws.amazon.com/braket/', accentRgb: '154, 122, 122' },
      { name: 'Rigetti', url: 'https://www.rigetti.com/', accentRgb: '120, 140, 170' },
      { name: 'Unitary Fund', url: 'https://unitary.foundation/', accentRgb: '122, 154, 154' },
      { name: 'arXiv quant-ph', url: 'https://arxiv.org/list/quant-ph/recent', accentRgb: '154, 138, 122' },
    ],
  },
];

const ResourceLink = ({ item }) => (
  <a
    href={item.url}
    target={item.url.startsWith('/') ? undefined : '_blank'}
    rel={item.url.startsWith('/') ? undefined : 'noopener noreferrer'}
    className="resource-item"
  >
    <div className="resource-info">
      <h3>{item.title}</h3>
      {item.meta && <p>{item.meta}</p>}
      <p className="resource-item__meta-small">{item.description}</p>
    </div>
  </a>
);

const BookCard = ({ item }) => (
  <a
    href={item.url}
    target="_blank"
    rel="noopener noreferrer"
    className="resource-item resource-item--book"
  >
    <div className="resource-info">
      <h3>{item.title}</h3>
      <p className="resource-info__accent">{item.authors}</p>
      <p className="resource-item__meta-small">{item.publisher}</p>
    </div>
    <p className="resource-item__desc">{item.description}</p>
  </a>
);

const ResourcePanel = ({ section }) => (
  <div className={`card resource-panel resource-panel--${section.variant}`}>
    <h2 className="resource-panel__title">{section.title}</h2>
    {section.blurb && <p className="resource-panel__blurb">{section.blurb}</p>}

    {section.layout === 'books' && (
      <div className="resources-books-grid">
        {section.items.map((item) => (
          <BookCard key={item.title} item={item} />
        ))}
      </div>
    )}

    {section.layout === 'pills' && (
      <div className="resources-quick-links">
        {section.items.map((item) => (
          <a
            key={item.name}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="resource-quick-link"
            style={{ '--rp-accent': item.accentRgb }}
          >
            {item.name}
          </a>
        ))}
      </div>
    )}

    {!section.layout && (
      <div className="resource-section">
        {section.items.map((item) => (
          <ResourceLink key={item.title} item={item} />
        ))}
      </div>
    )}
  </div>
);

const renderSectionRows = () => {
  const rows = [];
  let index = 0;

  while (index < RESOURCE_SECTIONS.length) {
    const section = RESOURCE_SECTIONS[index];
    const next = RESOURCE_SECTIONS[index + 1];

    if (section.width === 'half' && next?.width === 'half') {
      rows.push(
        <div key={`${section.id}-${next.id}`} className="resources-container">
          <ResourcePanel section={section} />
          <ResourcePanel section={next} />
        </div>
      );
      index += 2;
    } else {
      rows.push(<ResourcePanel key={section.id} section={section} />);
      index += 1;
    }
  }

  return rows;
};

const Resources = () => (
  <div className="resources-page">
    <div className="resources-stack">{renderSectionRows()}</div>
  </div>
);

export default Resources;
